import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import ScoreBadge from "@/components/ScoreBadge";
import { RichTextView } from "@/components/RichTextEditor";
import KanbanBoard from "@/components/KanbanBoard";
import AvailabilityPill from "@/components/candidate/AvailabilityPill";
import { computeAvailability } from "@/lib/availability";
import MessageThreadDialog from "@/components/MessageThreadDialog";
import InterviewQA from "@/components/candidate/InterviewQA";
import ResumePreviewDialog from "@/components/ResumePreviewDialog";
import CandidateProfileDialog from "@/components/CandidateProfileDialog";
import ActivityFeed from "@/components/ActivityFeed";
import SaveAsSeedDialog from "@/components/SaveAsSeedDialog";
import { SpotlightPickerBody } from "@/components/SpotlightPicker";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Upload, MapPin, Briefcase, AlertTriangle, FileText, MessageSquare, CheckCircle2, XCircle, ChevronLeft, User2, Link as LinkIcon, Pencil, Share2, Copy, LayoutGrid, List as ListIcon, Calendar, Flag, History, Eye, BarChart3, Lightbulb, Wallet, Sparkles, RefreshCw, Target } from "lucide-react";
import { formatCompCurrency, budgetBadgeClass, budgetLabel } from "@/lib/budget";

import CandidateAccordion from "@/components/candidate/CandidateAccordion";
import JobApplicantsTable from "@/components/JobApplicantsTable";
import { useDocsGateStageChange } from "@/lib/useDocsGateStageChange";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

/**
 * CandidatePanel — replaces the legacy two-pane in-page panel.
 *
 * Renders the SAME `CandidateAccordion` that the popup uses, so the
 * candidate-detail surface is identical whether the recruiter opens it
 * from the Kanban / list / Preview button / Full profile link.
 *
 * We fetch the full candidate doc on mount so applications, screening
 * profile, audit etc are all available to the accordion.
 */
function CandidatePanel({ candidate, application, onClose, onMessage }) {
    const [hydrated, setHydrated] = useState(null);
    const { user } = useAuth();

    const refresh = useCallback(async () => {
        if (!candidate?.id) return;
        try {
            const { data } = await api.get(`/candidates/${candidate.id}`);
            setHydrated(data);
        } catch (err) { toast.error(formatApiError(err)); }
    }, [candidate?.id]);

    useEffect(() => { refresh(); }, [refresh]);

    const c = hydrated || candidate;
    return (
        <div className="space-y-4" data-testid="candidate-detail-panel">
            <div className="flex items-center gap-3 flex-wrap">
                <button onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="close-candidate-panel">
                    <ChevronLeft className="w-4 h-4" /> Back to JD
                </button>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest font-mono text-ikb">Candidate</div>
                    <div className="font-display font-black text-xl sm:text-2xl truncate">{c?.name || "Unknown"}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <ScoreBadge score={application?.match_score || 0} size="md" status={application?.scoring_status === "ai_unavailable" ? "ai_unavailable" : null} processing={application?.scoring_status === "processing"} />
                    <button
                        type="button"
                        onClick={() => onMessage?.(c)}
                        className="text-xs font-mono uppercase tracking-widest border border-border px-3 py-2 hover:bg-secondary inline-flex items-center gap-1.5"
                        data-testid="message-candidate-btn"
                    >
                        <MessageSquare className="w-3.5 h-3.5" /> Message
                    </button>
                    <Link to={`/candidates/${c?.id}`} className="text-xs font-mono uppercase tracking-widest border border-border px-3 py-2 hover:bg-secondary" data-testid="open-full-candidate">
                        Full profile →
                    </Link>
                </div>
            </div>
            {!hydrated ? (
                <div className="border border-dashed border-border bg-white p-12 text-center text-xs font-mono text-muted-foreground">Loading candidate…</div>
            ) : (
                <CandidateAccordion
                    candidate={c}
                    focusedApp={application}
                    qaApp={application}
                    onReloaded={refresh}
                    currentUserId={user?.id}
                    isOwner={user?.role === "employer"}
                    storageKey={`panel-${c.id}`}
                />
            )}
        </div>
    );
}


export default function JobView() {
    const { id } = useParams();
    const [job, setJob] = useState(null);
    const [apps, setApps] = useState([]);
    const [selected, setSelected] = useState(null); // legacy — kept as anchor for stage-drop optimistic updates. Rendering redirects into <CandidateProfileDialog>.
    // Effect: whenever `selected` is set (e.g., from kanban card or list-row click),
    // open the unified modern CandidateProfileDialog instead of the outdated inline
    // panel. Runs after render so we don't trigger a set-state-during-render warning.
    useEffect(() => {
        if (selected && selected.candidate) {
            setProfilePreview({
                candidateId: selected.candidate.id || selected.candidate_id,
                applicationId: selected.id,
            });
            setSelected(null);
        }
    }, [selected]);
    const [uploading, setUploading] = useState(false);
    const [uploadPct, setUploadPct] = useState(0);
    const [sort, setSort] = useState("score");
    const [budget, setBudget] = useState("");
    const [availability, setAvailability] = useState("");   // "", "now", "7", "15", "30", "60", "unknown"
    const [stageFilter, setStageFilter] = useState("");     // Phase 158 — Application-stage filter
    const [voteFilter, setVoteFilter] = useState("");       // Phase 158 — Interested/Maybe/Not filter
    const [stages, setStages] = useState([]);
    // Doc Freshness gate — warn/block on stage change past a gate stage
    // when required employment docs are missing.
    const { changeStage: docsGateChangeStage, modal: docsGateModal } = useDocsGateStageChange();

    // Client-side filters on application list using the captured screening_profile
    // joining signals (server filter handles budget separately). Stage + Interest
    // filters here keep the entire visible list, including Prev/Next navigation in
    // the Profile and Resume Preview dialogs, in sync with what the recruiter sees.
    const visibleApps = useMemo(() => {
        let list = apps || [];
        if (availability) {
            list = list.filter((a) => {
                const av = computeAvailability(a.candidate || {}, a);
                if (availability === "unknown") return av.date == null;
                if (availability === "now") return av.date != null && (av.daysOut ?? 0) <= 0;
                const cap = parseInt(availability, 10);
                return av.date != null && (av.daysOut ?? Infinity) <= cap;
            });
        }
        if (stageFilter) {
            list = list.filter((a) => (a.stage || "Applied") === stageFilter);
        }
        if (voteFilter) {
            list = list.filter((a) => (a.vote_summary?.my_vote || "") === voteFilter);
        }
        return list;
    }, [apps, availability, stageFilter, voteFilter]);
    const [view, setView] = useState("list"); // "list" | "kanban" | "jd" | "activity"
    const [messageCandidate, setMessageCandidate] = useState(null);
    const [previewCandidate, setPreviewCandidate] = useState(null);
    const [profilePreview, setProfilePreview] = useState(null); // {candidateId, applicationId}
    const [saveAsSeedOpen, setSaveAsSeedOpen] = useState(false);
    const fileRef = useRef(null);

    const loadAll = useCallback(async () => {
        const params = { sort };
        if (budget) params.budget = budget;
        const [j, a, s] = await Promise.all([
            api.get(`/jobs/${id}`),
            api.get(`/jobs/${id}/applications`, { params }),
            api.get(`/company/pipeline-stages`),
        ]);
        setJob(j.data);
        setApps(a.data || []);
        setStages(s.data || []);
    }, [id, sort, budget]);

    useEffect(() => { loadAll().catch((e) => toast.error(formatApiError(e))); }, [loadAll]);

    const onFiles = async (files) => {
        if (!files || !files.length) return;
        setUploading(true);
        setUploadPct(0);

        // Batch into small groups so each HTTP request stays under proxy/body limits
        // (production ingress typically caps multipart at ~32MB and times out >60s on
        // slow connections). We send 3 files per request in parallel, max 3 requests
        // in flight at once. Each request returns in a few seconds because the backend
        // hands the heavy AI work off to a background task.
        const list = Array.from(files);
        const BATCH = 3;
        const batches = [];
        for (let i = 0; i < list.length; i += BATCH) batches.push(list.slice(i, i + BATCH));

        const totalBytes = list.reduce((s, f) => s + (f.size || 0), 0) || 1;
        const sentBytes = new Array(batches.length).fill(0);

        const sendBatch = async (batch, idx) => {
            const fd = new FormData();
            batch.forEach((f) => fd.append("files", f));
            const batchTotal = batch.reduce((s, f) => s + (f.size || 0), 0);
            const { data } = await api.post(`/jobs/${id}/upload-resumes`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 120000,
                onUploadProgress: (evt) => {
                    sentBytes[idx] = Math.min(batchTotal, evt.loaded || 0);
                    const overall = sentBytes.reduce((a, b) => a + b, 0);
                    setUploadPct(Math.round((overall / totalBytes) * 100));
                },
            });
            return data;
        };

        // Limit concurrency so we don't fire 10+ simultaneous requests
        const CONCURRENCY = 3;
        const allResults = [];
        try {
            for (let i = 0; i < batches.length; i += CONCURRENCY) {
                const wave = batches.slice(i, i + CONCURRENCY);
                // eslint-disable-next-line no-await-in-loop
                const settled = await Promise.allSettled(wave.map((b, k) => sendBatch(b, i + k)));
                settled.forEach((s, k) => {
                    if (s.status === "fulfilled") {
                        allResults.push(...(s.value?.results || []));
                    } else {
                        wave[k].forEach((f) => allResults.push({ filename: f.name, ok: false, error: formatApiError(s.reason) }));
                    }
                });
            }
            setUploadPct(100);
            const ok = allResults.filter((r) => r.ok).length;
            const fail = allResults.filter((r) => !r.ok).length;
            if (ok && !fail) toast.success(`${ok} resume${ok === 1 ? "" : "s"} uploaded — AI parsing now running in the background. Scores will appear as each one finishes (track in the AI Queue widget).`);
            else if (ok && fail) toast.success(`${ok} uploaded, ${fail} rejected. Hover the failed rows for the reason.`);
            else toast.error("No resumes were accepted. Check the file types/sizes and try again.");
            await loadAll();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    // Phase 56.1 — the previous 4s-interval auto-poll was the root cause of the
    // "stuck in refresh mode" symptom on production. It re-fetched every
    // application from /jobs/{id}/applications every 4 seconds for up to ~3
    // minutes whenever ANY row was still scoring, which kept the Kanban
    // re-rendering for the entire duration of a multi-resume parse run.
    //
    // We now listen for the global `ai-queue-completed` window event emitted
    // by `AIQueueWidget` whenever a parse finishes. If the completion is for a
    // candidate in THIS job, we surface a small "N new results — refresh"
    // pill so recruiters can pull the fresh data on demand (one click, one
    // network call) instead of paying for a 4s polling storm.
    const [newResultsCount, setNewResultsCount] = useState(0);
    useEffect(() => {
        const handler = (e) => {
            const detail = e?.detail || {};
            // Only count completions tied to this job (best-effort by job_id;
            // some uploads may not carry job_id so we accept those too if the
            // candidate ends up in this job's applications list — handled by
            // the manual refresh).
            if (!detail.job_id || detail.job_id === id) {
                setNewResultsCount((c) => c + 1);
            }
        };
        window.addEventListener("ai-queue-completed", handler);
        return () => window.removeEventListener("ai-queue-completed", handler);
    }, [id]);

    const onRefreshForNewResults = useCallback(async () => {
        setNewResultsCount(0);
        try { await loadAll(); }
        catch { /* swallow — toast already surfaced */ }
    }, [loadAll]);

    if (!job) {
        return <div className="p-8 text-muted-foreground font-mono">Loading job...</div>;
    }

    const publicUrl = job.slug ? `${window.location.origin}/j/${job.slug}` : null;
    const copyPublicLink = async () => {
        if (!publicUrl) return;
        try {
            await navigator.clipboard.writeText(publicUrl);
            toast.success("Public apply link copied");
        } catch {
            toast.error("Couldn't copy — link: " + publicUrl);
        }
    };

    const updateStage = async (appId, stage) => {
        const app = (apps || []).find((a) => a.id === appId);
        const candId = app?.candidate?.id || app?.candidate_id;
        const candName = app?.candidate?.name || "";
        const res = await docsGateChangeStage(appId, stage, {
            candidate_id: candId,
            candidate_name: candName,
        });
        if (res?.ok) {
            setApps((prev) => prev.map((a) => a.id === appId ? { ...a, stage } : a));
            if (selected?.id === appId) setSelected({ ...selected, stage });
        }
    };

    const cloneJob = async () => {
        if (!window.confirm("Create a draft copy of this job?")) return;
        try {
            const { data } = await api.post(`/jobs/${id}/clone`);
            toast.success("Cloned — opening draft");
            window.location.href = `/jobs/${data.id}/edit`;
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const dueIn = (() => {
        if (!job.due_date) return null;
        const d = new Date(job.due_date.length === 10 ? job.due_date + "T00:00:00" : job.due_date);
        if (Number.isNaN(d.getTime())) return null;
        return Math.ceil((d.getTime() - Date.now()) / 86400000);
    })();
    const priority = (job.priority || "normal").toLowerCase();

    return (
        <div className="bg-background min-h-[calc(100vh-4rem)]">
            <header className="border-b border-border bg-white">
                <div className="max-w-screen-2xl mx-auto px-3 sm:px-6 py-4 sm:py-5 flex items-start justify-between gap-4 sm:gap-6 flex-wrap">
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest font-mono text-ikb">Job</div>
                        <div className="flex items-center gap-2 sm:gap-3 flex-wrap mt-1">
                            <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight">{job.title}</h1>
                            <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${
                                job.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : job.status === "draft" ? "bg-slate-100 text-slate-700 border-slate-300"
                                : job.status === "paused" ? "bg-amber-50 text-amber-700 border-amber-200"
                                : "bg-rose-50 text-rose-700 border-rose-200"
                            }`} data-testid="job-status-badge">
                                {job.status}
                            </span>
                            {priority !== "normal" && (
                                <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border inline-flex items-center gap-1 ${
                                    priority === "urgent" ? "text-rose-700 border-rose-300 bg-rose-50"
                                    : priority === "high" ? "text-amber-700 border-amber-300 bg-amber-50"
                                    : "text-slate-500 border-slate-200 bg-white"
                                }`} data-testid="job-priority-badge">
                                    <Flag className="w-3 h-3" /> {priority}
                                </span>
                            )}
                            {/* Spotlight badge — clickable, opens the SpotlightPicker popover
                             *  so recruiters can extend/change the spotlight in one click. */}
                            <SpotlightBadge job={job} onSaved={(payload) => setJob((j) => ({ ...j, ...payload }))} />
                            {dueIn !== null && (
                                <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border inline-flex items-center gap-1 ${
                                    dueIn < 0 ? "bg-rose-50 text-rose-700 border-rose-300"
                                    : dueIn <= 7 ? "bg-amber-50 text-amber-700 border-amber-200"
                                    : "bg-white text-muted-foreground border-border"
                                }`}>
                                    <Calendar className="w-3 h-3" /> {dueIn < 0 ? `Overdue ${Math.abs(dueIn)}d` : `Due in ${dueIn}d`}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-3 text-xs font-mono text-muted-foreground mt-2 flex-wrap">
                            <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {job.location || "Remote"}</span>
                            <span>·</span> <span>{job.employment_type}</span>
                            <span>·</span> <span>{job.seniority}</span>
                            <span>·</span> <span>{job.experience_min}–{job.experience_max} YoE</span>
                            {(job.salary_min || job.salary_max) && (
                                <>
                                    <span>·</span>
                                    <span className="inline-flex items-center gap-1" data-testid="job-salary-band">
                                        <Wallet className="w-3 h-3" />
                                        {formatCompCurrency(job.salary_min, job.salary_currency, job.salary_period)} – {formatCompCurrency(job.salary_max, job.salary_currency, job.salary_period)}
                                    </span>
                                </>
                            )}
                            <span>·</span> <span>{apps.length} applicant{apps.length === 1 ? "" : "s"}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {publicUrl && (
                            <button onClick={copyPublicLink} className="text-[10px] sm:text-xs font-mono uppercase tracking-widest border border-border px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-secondary inline-flex items-center gap-1 sm:gap-1.5" data-testid="copy-public-link">
                                <Share2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> <span className="hidden sm:inline">Copy public link</span><span className="sm:hidden">Link</span>
                            </button>
                        )}
                        <Link to={`/jobs/${id}/edit`} className="text-[10px] sm:text-xs font-mono uppercase tracking-widest border border-border px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-secondary inline-flex items-center gap-1 sm:gap-1.5" data-testid="edit-job-link">
                            <Pencil className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> Edit
                        </Link>
                        <button onClick={cloneJob} className="text-[10px] sm:text-xs font-mono uppercase tracking-widest border border-border px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-secondary inline-flex items-center gap-1 sm:gap-1.5" data-testid="clone-job-btn">
                            <Copy className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> Clone
                        </button>
                        <button onClick={() => setSaveAsSeedOpen(true)} className="text-[10px] sm:text-xs font-mono uppercase tracking-widest border border-border px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-secondary inline-flex items-center gap-1 sm:gap-1.5" title="Turn this job's skills, must-haves and red-flags into a reusable org-wide template" data-testid="save-as-seed-btn">
                            <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> <span className="hidden sm:inline">Save as seed</span><span className="sm:hidden">Seed</span>
                        </button>
                        <input ref={fileRef} type="file" multiple accept=".pdf,.doc,.docx,.txt" hidden onChange={(e) => onFiles(e.target.files)} data-testid="file-input-hidden" />
                        <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-9 px-3 sm:min-w-[10rem]" data-testid="upload-resumes-btn">
                            <Upload className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">{uploading
                                ? (uploadPct < 100 ? `Uploading ${uploadPct}%` : "Dispatched →")
                                : "Upload resumes"}</span>
                            <span className="sm:hidden ml-1">{uploading ? `${uploadPct}%` : "Upload"}</span>
                        </Button>
                    </div>
                </div>

                {uploading && (
                    <div className="max-w-screen-2xl mx-auto px-3 sm:px-6 pb-2" data-testid="job-upload-progress">
                        <div className="h-1 bg-secondary border border-border overflow-hidden">
                            <div className="h-full bg-ikb transition-all duration-200" style={{ width: `${uploadPct}%` }} />
                        </div>
                    </div>
                )}

                <div className="max-w-screen-2xl mx-auto px-3 sm:px-6 flex items-center gap-1 border-t border-border overflow-x-auto">
                    {[
                        ["list", "List", ListIcon],
                        ["kanban", "Kanban", LayoutGrid],
                        ["gap", "Gap Map", Target],
                        ["jd", "Description", FileText],
                        ["analytics", "Analytics", BarChart3],
                        ["activity", "Activity", History],
                    ].map(([k, label, Icon]) => (
                        <button
                            key={k}
                            onClick={() => { setView(k); setSelected(null); }}
                            data-testid={`view-tab-${k}`}
                            className={`shrink-0 flex items-center gap-2 px-3 sm:px-4 py-2.5 text-xs font-mono uppercase tracking-widest border-b-2 -mb-px transition-colors ${
                                view === k ? "text-ikb border-ikb" : "text-foreground/80 border-transparent hover:text-foreground"
                            }`}
                        >
                            <Icon className="w-3.5 h-3.5" /> {label}
                        </button>
                    ))}
                    <div className="ml-auto py-2 shrink-0 flex items-center gap-2 flex-wrap">
                        <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} className="h-8 px-2 border border-border bg-white text-xs" data-testid="applicants-stage-filter">
                            <option value="">Stage: all</option>
                            {(stages || []).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                        <select value={voteFilter} onChange={(e) => setVoteFilter(e.target.value)} className="h-8 px-2 border border-border bg-white text-xs" data-testid="applicants-vote-filter">
                            <option value="">Interest: all</option>
                            <option value="interested">Interested (my vote)</option>
                            <option value="maybe">Maybe (my vote)</option>
                            <option value="not_interested">Not interested (my vote)</option>
                        </select>
                        <select value={availability} onChange={(e) => setAvailability(e.target.value)} className="h-8 px-2 border border-border bg-white text-xs" data-testid="applicants-availability-filter">
                            <option value="">Joining: any</option>
                            <option value="now">Available now</option>
                            <option value="7">≤ 7 days</option>
                            <option value="15">≤ 15 days</option>
                            <option value="30">≤ 30 days</option>
                            <option value="60">≤ 60 days</option>
                            <option value="unknown">Unknown</option>
                        </select>
                        <select value={budget} onChange={(e) => setBudget(e.target.value)} className="h-8 px-2 border border-border bg-white text-xs" data-testid="applicants-budget-filter">
                            <option value="">Budget: all</option>
                            <option value="at">In budget</option>
                            <option value="under">Under budget</option>
                            <option value="over">Over budget</option>
                            <option value="unknown">Currency mismatch</option>
                            <option value="missing">No expectations</option>
                        </select>
                        <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-8 px-2 border border-border bg-white text-xs" data-testid="applicants-sort">
                            <option value="score">Sort: AI score</option>
                            <option value="recruiter_rating">Recruiter rating</option>
                            <option value="experience">Experience</option>
                            <option value="recent">Most recent</option>
                            <option value="name">Name</option>
                        </select>
                    </div>
                </div>
            </header>

            <div className="max-w-screen-2xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
                {newResultsCount > 0 ? (
                    <button
                        onClick={onRefreshForNewResults}
                        className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-300 text-emerald-800 font-mono text-[11px] uppercase tracking-widest hover:bg-emerald-100"
                        data-testid="new-results-banner"
                    >
                        <RefreshCw className="w-3.5 h-3.5" />
                        {newResultsCount} new result{newResultsCount === 1 ? "" : "s"} ready — click to refresh
                    </button>
                ) : null}
                {view === "kanban" ? (
                    apps.length === 0 ? (
                        <div className="border border-dashed border-border p-16 text-center bg-white">
                            <h3 className="font-display font-bold text-xl">No candidates yet</h3>
                            <p className="text-sm text-foreground/80 mt-2 mb-6">Upload resumes and the Kanban will populate.</p>
                            <Button onClick={() => fileRef.current?.click()} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white">
                                <Upload className="w-4 h-4 mr-2" /> Upload resumes
                            </Button>
                        </div>
                    ) : (
                        <KanbanBoard
                            apps={visibleApps}
                            stages={stages}
                            onCardClick={(a) => setProfilePreview({ candidateId: a.candidate?.id || a.candidate_id, applicationId: a.id })}
                            onStageChange={updateStage}
                            onPreviewResume={(c) => setPreviewCandidate(c)}
                            onPreviewProfile={(app) => setProfilePreview({ candidateId: app.candidate?.id || app.candidate_id, applicationId: app.id })}
                            onRetryParse={async (app) => {
                                const cid = app.candidate?.id || app.candidate_id;
                                if (!cid) return;
                                try {
                                    await api.post(`/candidates/${cid}/reparse`);
                                    toast.success("Re-parsing started — track in the AI Queue widget");
                                    // Optimistic flip so the Kanban card chip updates immediately
                                    setApps((prev) => prev.map((a) => (a.candidate?.id === cid || a.candidate_id === cid)
                                        ? { ...a, candidate: { ...(a.candidate || {}), processing_status: "processing", parse_error: null }, scoring_status: "processing" }
                                        : a
                                    ));
                                } catch (err) {
                                    toast.error(formatApiError(err));
                                }
                            }}
                        />
                    )
                ) : view === "activity" ? (
                    <div className="bg-white border border-border p-6">
                        <ActivityFeed targetType="job" targetId={id} />
                    </div>
                ) : view === "analytics" ? (
                    <JobAnalytics jobId={id} />
                ) : view === "gap" ? (
                    <JobGapHeatmap jobId={id} />
                ) : view === "list" ? (
                    <JobApplicantsTable
                        apps={visibleApps}
                        stages={stages}
                        onOpenProfile={(a) => setProfilePreview({ candidateId: a.candidate?.id || a.candidate_id, applicationId: a.id })}
                        onStageChange={updateStage}
                        onRefresh={loadAll}
                        jobId={id}
                        job={job}
                        jobTitle={job?.title || "job"}
                    />
                ) : (
                    <div className="bg-white border border-border p-8 space-y-8">
                        <div className="prose max-w-none">
                            <h3 className="font-display font-bold text-sm uppercase tracking-widest text-muted-foreground">Description</h3>
                            <div className="mt-2 jd-readable"><RichTextView html={job.description} /></div>
                        </div>

                        {(job.required_skills || []).length > 0 && (
                            <section>
                                <h3 className="font-display font-bold text-sm uppercase tracking-widest text-muted-foreground mb-3">Required skills</h3>
                                <div className="flex flex-wrap gap-2">
                                    {job.required_skills.map((s) => (
                                        <span key={s} className="text-sm px-2.5 py-1 border border-border bg-white font-medium">{s}</span>
                                    ))}
                                </div>
                            </section>
                        )}

                        {(job.must_haves || []).length > 0 && (
                            <section>
                                <h3 className="font-display font-bold text-sm uppercase tracking-widest text-muted-foreground mb-3">Must-haves</h3>
                                <ul className="space-y-1.5 list-disc pl-5">
                                    {job.must_haves.map((m, i) => <li key={i} className="text-sm">{m}</li>)}
                                </ul>
                            </section>
                        )}

                        {(job.red_flags || []).length > 0 && (
                            <section>
                                <h3 className="font-display font-bold text-sm uppercase tracking-widest text-destructive mb-3">Red flags to avoid</h3>
                                <ul className="space-y-1.5 list-disc pl-5 text-destructive">
                                    {job.red_flags.map((r, i) => <li key={i} className="text-sm">{r}</li>)}
                                </ul>
                            </section>
                        )}
                    </div>
                )}
            </div>

            {messageCandidate && (
                <MessageThreadDialog
                    candidate={messageCandidate}
                    onClose={() => setMessageCandidate(null)}
                />
            )}

            {previewCandidate && (
                <ResumePreviewDialog
                    open={!!previewCandidate}
                    onClose={() => setPreviewCandidate(null)}
                    candidate={previewCandidate}
                />
            )}

            {profilePreview && (() => {
                // Build the siblings list from the currently visible (filtered + sorted-by-score default)
                // list so Prev/Next cycles exactly through what the recruiter sees.
                const sibs = (visibleApps || []).map((a) => ({
                    candidateId: a.candidate?.id || a.candidate_id,
                    applicationId: a.id,
                }));
                const idx = sibs.findIndex((s) => s.applicationId === profilePreview.applicationId);
                return (
                    <CandidateProfileDialog
                        open={!!profilePreview}
                        onClose={() => setProfilePreview(null)}
                        candidateId={profilePreview.candidateId}
                        applicationId={profilePreview.applicationId}
                        siblings={sibs.length > 1 ? sibs : null}
                        siblingIndex={idx >= 0 ? idx : null}
                        onNavigate={(i) => {
                            const next = sibs[i];
                            if (next) setProfilePreview(next);
                        }}
                        onChanged={() => { try { loadAll?.(); } catch { /* parent may use different reload */ } }}
                    />
                );
            })()}

            <SaveAsSeedDialog
                open={saveAsSeedOpen}
                onClose={() => setSaveAsSeedOpen(false)}
                job={job}
                onSaved={() => toast.success("Find it under JD Seeds in the sidebar")}
            />
            {docsGateModal}
        </div>
    );
}


function JobAnalytics({ jobId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get(`/jobs/${jobId}/analytics`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    }, [jobId]);
    if (loading) return <div className="bg-white border border-border p-8 text-sm font-mono text-foreground/70">Loading analytics…</div>;
    if (!data) return null;
    const kpis = [
        ["Public views", data.views_total, `${data.views_30d || 0} in last 30d`],
        ["Applications", data.applications_total, `${data.applications_30d || 0} in last 30d`],
        ["Conversion rate", `${data.conversion_rate || 0}%`, "applied / viewed"],
    ];
    const funnel = data.funnel || {};
    const totalApps = data.applications_total || 1;
    const stagesOrdered = ["Applied", "Screening", "Interview", "Offer", "Hired", "Rejected", "Withdrawn"];
    return (
        <div className="bg-white border border-border p-4 sm:p-5 lg:p-6 space-y-6" data-testid="job-analytics">
            <h2 className="font-display font-bold text-xl">JD performance</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-border">
                {kpis.map(([label, value, hint]) => (
                    <div key={label} className="bg-white p-4">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">{label}</div>
                        <div className="text-2xl font-display font-black mt-1">{value}</div>
                        <div className="text-xs text-foreground/70 mt-1">{hint}</div>
                    </div>
                ))}
            </div>

            <div>
                <h3 className="font-display font-bold text-sm uppercase tracking-widest mb-3">Pipeline funnel</h3>
                {Object.keys(funnel).length === 0 ? (
                    <div className="text-sm text-foreground/70 italic">No applications yet.</div>
                ) : (
                    <ul className="space-y-2">
                        {stagesOrdered.filter((s) => funnel[s]).concat(Object.keys(funnel).filter((s) => !stagesOrdered.includes(s))).map((stage) => {
                            const n = funnel[stage] || 0;
                            const pct = Math.round((n / totalApps) * 100);
                            return (
                                <li key={stage} className="flex items-center gap-3">
                                    <span className="text-xs font-mono uppercase tracking-widest w-32 shrink-0">{stage}</span>
                                    <div className="flex-1 bg-secondary h-6 relative overflow-hidden">
                                        <div className="bg-ikb h-full transition-all" style={{ width: `${pct}%` }} />
                                    </div>
                                    <span className="text-xs font-mono w-16 text-right">{n} · {pct}%</span>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            <div>
                <h3 className="font-display font-bold text-sm uppercase tracking-widest mb-3">Application sources</h3>
                {(data.sources || []).length === 0 ? (
                    <div className="text-sm text-foreground/70 italic">No applications yet.</div>
                ) : (
                    <ul className="space-y-1.5">
                        {data.sources.map((s) => (
                            <li key={s.source} className="flex items-center justify-between text-sm border border-border bg-white px-3 py-2">
                                <span className="font-mono uppercase tracking-widest text-xs">{s.source}</span>
                                <span className="font-mono text-xs">{s.count}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// R13 — Job-level gap heatmap
// Matrix of applications × required skills, colored by evaluation status.
// ═══════════════════════════════════════════════════════════════════════════
function JobGapHeatmap({ jobId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [includePool, setIncludePool] = useState("active");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/skill-center/jobs/${jobId}/gap-heatmap`, { params: { include_pool: includePool } });
            setData(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [jobId, includePool]);

    useEffect(() => { load(); }, [load]);

    if (loading && !data) return <div className="bg-white border border-border p-12 text-center text-sm text-muted-foreground">Building heatmap…</div>;
    if (!data) return null;

    const cellCls = (status) => {
        if (status === "possesses") return "bg-emerald-500 hover:bg-emerald-600";
        if (status === "partial") return "bg-amber-400 hover:bg-amber-500";
        if (status === "gap") return "bg-rose-400 hover:bg-rose-500";
        return "bg-slate-200 hover:bg-slate-300";
    };
    const cellSym = (status) => ({possesses: "✓", partial: "◐", gap: "✗", unknown: "·"}[status] || "·");

    if (data.total_applications === 0 || data.skills.length === 0) {
        const msg = data.skills.length === 0
            ? "This job has no required skills yet. Add skills on the Description tab to see the coverage matrix."
            : "No applications yet for this job. Once candidates apply, they\u2019ll appear here.";
        return (
            <div className="bg-white border border-border p-12 text-center text-sm text-muted-foreground" data-testid="job-gap-heatmap-empty">
                {msg}
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="job-gap-heatmap">
            <div className="border border-border bg-white p-3 flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
                    {data.total_applications} candidate{data.total_applications === 1 ? "" : "s"} × {data.skills.length} skill{data.skills.length === 1 ? "" : "s"}
                </span>
                <select
                    value={includePool}
                    onChange={(e) => setIncludePool(e.target.value)}
                    className="h-8 px-2 border border-border bg-white text-xs"
                    data-testid="job-gap-heatmap-pool"
                >
                    <option value="active">Active pool</option>
                    <option value="all">Include archived</option>
                </select>
                <Button variant="outline" className="rounded-none h-8" onClick={load} data-testid="job-gap-heatmap-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                </Button>
                <div className="ml-auto flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest">
                    <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-emerald-500" /> Possesses</span>
                    <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-amber-400" /> Partial</span>
                    <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-rose-400" /> Gap</span>
                    <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-slate-200" /> Unknown</span>
                </div>
            </div>

            {(data.skill_health?.declining_required?.length > 0 || data.skill_health?.under_covered?.length > 0) && (
                <div className="grid sm:grid-cols-2 gap-2" data-testid="job-gap-heatmap-alerts">
                    {data.skill_health?.declining_required?.length > 0 && (
                        <div className="border border-amber-400 bg-amber-50 p-2 text-xs" data-testid="job-gap-declining">
                            <b className="font-mono uppercase tracking-widest text-[10px] text-amber-800">⚠ Declining required skills</b>
                            <div className="mt-1 flex flex-wrap gap-1">
                                {data.skill_health.declining_required.map((s) => (
                                    <span key={s} className="text-[11px] font-mono border border-amber-500 bg-white px-1">
                                        {s}{data.coverage[s]?.trend_replaced_by && <span className="text-emerald-700"> → {data.coverage[s].trend_replaced_by}</span>}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    {data.skill_health?.under_covered?.length > 0 && (
                        <div className="border border-rose-400 bg-rose-50 p-2 text-xs" data-testid="job-gap-under-covered">
                            <b className="font-mono uppercase tracking-widest text-[10px] text-rose-800">✗ Under-covered in the pool</b>
                            <div className="mt-1 flex flex-wrap gap-1">
                                {data.skill_health.under_covered.map((s) => (
                                    <span key={s} className="text-[11px] font-mono border border-rose-500 bg-white px-1" title={`Only ${data.coverage[s]?.counts?.possesses || 0} of ${data.total_applications} candidates match`}>
                                        {s} · {data.coverage[s]?.coverage_pct}%
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="border border-border bg-white overflow-x-auto" data-testid="job-gap-heatmap-grid">
                <table className="min-w-full text-xs">
                    <thead className="bg-secondary/40">
                        <tr>
                            <th className="sticky left-0 bg-secondary/40 border-r border-border px-3 py-2 text-left font-mono uppercase tracking-widest text-[10px] text-muted-foreground z-10">
                                Candidate
                            </th>
                            <th className="border-r border-border px-2 py-2 text-center font-mono uppercase tracking-widest text-[10px] text-muted-foreground">Score</th>
                            {data.skills.map((s) => {
                                const cov = data.coverage[s] || {};
                                return (
                                    <th key={s} className="border-r border-border px-1 py-2 text-center min-w-[80px]">
                                        <div className="text-[11px] font-medium truncate max-w-[100px] mx-auto" title={s}>{s}</div>
                                        <div className="text-[9px] font-mono text-muted-foreground">{cov.coverage_pct}% ({cov.counts?.possesses || 0}/{data.total_applications})</div>
                                        {cov.trend_status && ["declining","obsolete"].includes(cov.trend_status) && (
                                            <div className="text-[9px] text-amber-700 mt-0.5">⚠ {cov.trend_status}</div>
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {data.candidates.map((c) => (
                            <tr key={c.application_id} className="border-t border-border" data-testid={`job-gap-row-${c.application_id}`}>
                                <td className="sticky left-0 bg-white border-r border-border px-3 py-1.5 z-10">
                                    <div className="font-medium truncate max-w-[180px]" title={c.name}>{c.name}</div>
                                    {c.current_role && <div className="text-[10px] text-muted-foreground truncate max-w-[180px]" title={c.current_role}>{c.current_role}</div>}
                                </td>
                                <td className="border-r border-border px-2 py-1.5 text-center">
                                    <span className="text-[11px] font-mono font-bold">{c.match_score ?? "—"}</span>
                                </td>
                                {c.cells.map((cell, i) => (
                                    <td
                                        key={i}
                                        className={`border-r border-border text-center p-0 cursor-help ${cellCls(cell.status)}`}
                                        title={`${data.skills[i]} — ${cell.status}${cell.confidence !== null && cell.confidence !== undefined ? ` (${Math.round((cell.confidence || 0) * 100)}%)` : ""}${cell.fallback ? " · declared" : ""}`}
                                        data-testid={`job-gap-cell-${c.application_id}-${i}`}
                                    >
                                        <span className="inline-block w-full h-8 leading-8 text-white font-mono text-sm">{cellSym(cell.status)}</span>
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <SourcingSuggestionsPanel jobId={jobId} focusHint={data.skill_health?.under_covered || []} />
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// Sourcing Suggestions — pulls talent-pool candidates who have the under-
// covered skills but haven't applied yet. Rendered below the Gap Heatmap.
// ═══════════════════════════════════════════════════════════════════════════
function SourcingSuggestionsPanel({ jobId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [openSkill, setOpenSkill] = useState(null);
    // invited: {[candidate_id]: {invitation_id, email_status, sent_at}}
    const [invited, setInvited] = useState({});
    // inviteTarget: {candidate, skill} — controls the modal
    const [inviteTarget, setInviteTarget] = useState(null);

    const loadInvitations = useCallback(async () => {
        try {
            const { data } = await api.get(`/skill-center/jobs/${jobId}/invitations`);
            const map = {};
            (data.items || []).forEach((it) => { map[it.candidate_id] = it; });
            setInvited(map);
        } catch { /* silent — invitation history is best-effort */ }
    }, [jobId]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/skill-center/jobs/${jobId}/sourcing-suggestions`);
            setData(data);
            const first = (data.suggestions || []).find((s) => s.candidates?.length > 0);
            if (first) setOpenSkill(first.skill);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [jobId]);

    useEffect(() => { load(); loadInvitations(); }, [load, loadInvitations]);

    const handleInvited = (invitationDoc) => {
        setInvited((prev) => ({ ...prev, [invitationDoc.candidate_id]: invitationDoc }));
    };

    if (loading) return <div className="bg-white border border-border p-6 text-center text-sm text-muted-foreground">Building sourcing suggestions…</div>;
    if (!data) return null;

    const suggestions = data.suggestions || [];
    const focusSkills = data.focus_skills || [];
    const anyCandidates = suggestions.some((s) => (s.candidates || []).length > 0);
    const noFocus = focusSkills.length === 0 && suggestions.length === 0;

    return (
        <div className="border border-border bg-white" data-testid="job-sourcing-panel">
            <div className="px-3 py-2 border-b border-border bg-secondary/30 flex items-center gap-2">
                <span className="text-[11px] font-mono uppercase tracking-widest text-ikb">
                    Sourcing suggestions from your talent pool
                </span>
                <span className="text-[10px] text-muted-foreground ml-2">
                    Candidates you already have on file who match the under-covered skills but haven&apos;t applied yet
                </span>
                <Button variant="outline" size="sm" className="rounded-none h-7 ml-auto" onClick={load} data-testid="job-sourcing-refresh">
                    <RefreshCw className="w-3 h-3" />
                </Button>
            </div>
            {(noFocus || !anyCandidates) && (
                <div className="p-6 text-center text-xs text-muted-foreground" data-testid="job-sourcing-empty">
                    {noFocus
                        ? "No under-covered skills detected for this job. Add required skills or check back once applicants come in."
                        : "Your talent pool has no unapplied candidates with these skills. Time to source externally — try Talent Search."}
                </div>
            )}
            <div className="divide-y divide-border">
                {suggestions.map((s) => {
                    const isOpen = openSkill === s.skill;
                    const skillSlug = s.skill.replace(/\s+/g, "-").toLowerCase();
                    const panelId = `job-sourcing-skill-panel-${skillSlug}`;
                    return (
                        <div key={s.skill} data-testid={`job-sourcing-skill-${skillSlug}`}>
                            <button
                                type="button"
                                onClick={() => setOpenSkill(isOpen ? null : s.skill)}
                                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-secondary/20 text-left"
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                            >
                                <span className="text-sm font-medium">{s.skill}</span>
                                {s.trend_status && ["declining", "obsolete"].includes(s.trend_status) && (
                                    <span className="text-[10px] font-mono uppercase tracking-widest border border-amber-500 bg-amber-50 text-amber-800 px-1">
                                        {s.trend_status}{s.trend_replaced_by ? ` → ${s.trend_replaced_by}` : ""}
                                    </span>
                                )}
                                <span className="text-[10px] font-mono text-muted-foreground ml-auto">
                                    {(s.candidates || []).length} suggested · {s.pool_count} in pool · {s.already_applied_with_skill} already applied
                                </span>
                                <span className="text-[10px] font-mono text-ikb" aria-hidden="true">{isOpen ? "−" : "+"}</span>
                                <span className="sr-only">{isOpen ? "Collapse" : "Expand"} {s.skill} suggestions</span>
                            </button>
                            {isOpen && (
                                <div className="border-t border-border" id={panelId}>
                                    {(s.candidates || []).length === 0 ? (
                                        <div className="p-3 text-xs text-muted-foreground">
                                            No unapplied talent-pool candidates match this skill. All {s.pool_count} matching candidates have already applied or the pool is dry.
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-border">
                                            {(s.candidates || []).map((c) => {
                                                const inv = invited[c.id];
                                                return (
                                                <div key={c.id} className="p-2.5 flex items-start gap-3 hover:bg-secondary/10" data-testid={`job-sourcing-cand-${c.id}`}>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <a
                                                                href={`/candidates?open=${c.id}`}
                                                                className="text-sm font-medium text-ikb hover:underline truncate max-w-[240px]"
                                                                title={c.name}
                                                            >{c.name}</a>
                                                            {c.current_role && <span className="text-[11px] text-muted-foreground truncate max-w-[220px]">{c.current_role}</span>}
                                                            <span className="text-[10px] font-mono text-muted-foreground">{c.total_experience_years} yrs</span>
                                                            {c.location && <span className="text-[10px] font-mono text-muted-foreground">· {c.location}</span>}
                                                            <span className="text-[10px] font-mono border border-emerald-500 bg-emerald-50 text-emerald-800 px-1" title="Fit score based on skill overlap + YoE + summary">
                                                                fit {c.fit_score}
                                                            </span>
                                                            {inv && (
                                                                <span
                                                                    className="text-[10px] font-mono uppercase tracking-widest border border-ikb/40 bg-ikb/5 text-ikb px-1"
                                                                    title={inv.sent_at ? `Invited on ${new Date(inv.sent_at).toLocaleDateString()}` : "Invitation logged"}
                                                                    data-testid={`job-sourcing-invited-badge-${c.id}`}
                                                                >
                                                                    {inv.email_status === "sent" ? "Invited" : "Invite logged"}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {c.matched_focus_skills?.length > 1 && (
                                                            <div className="text-[10px] text-muted-foreground mt-0.5">
                                                                Matches: {c.matched_focus_skills.slice(0, 6).map((sk) => (
                                                                    <span key={sk} className="inline-block border border-border bg-white px-1 mr-1">{sk}</span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {c.ai_summary && <div className="text-[11px] text-foreground/70 mt-1 line-clamp-2">{c.ai_summary}</div>}
                                                    </div>
                                                    <div className="flex flex-col gap-1 shrink-0 self-center">
                                                        <Button
                                                            size="sm"
                                                            className="rounded-none h-7 text-[10px] font-mono uppercase tracking-widest bg-ikb hover:bg-ikb/90"
                                                            onClick={() => setInviteTarget({ candidate: c, skill: s.skill })}
                                                            data-testid={`job-sourcing-invite-${c.id}`}
                                                        >
                                                            {inv ? "Re-invite" : "Invite to apply"}
                                                        </Button>
                                                        <a
                                                            href={`/candidates?open=${c.id}`}
                                                            className="text-[10px] font-mono text-center text-muted-foreground hover:text-ikb underline"
                                                            data-testid={`job-sourcing-view-${c.id}`}
                                                        >
                                                            View profile
                                                        </a>
                                                    </div>
                                                </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {inviteTarget && (
                <InviteToApplyDialog
                    jobId={jobId}
                    candidate={inviteTarget.candidate}
                    skill={inviteTarget.skill}
                    existing={invited[inviteTarget.candidate.id]}
                    onClose={() => setInviteTarget(null)}
                    onSent={(doc) => {
                        handleInvited(doc);
                        setInviteTarget(null);
                    }}
                />
            )}
        </div>
    );
}



// ═══════════════════════════════════════════════════════════════════════════
// InviteToApplyDialog — editable preview of the sourcing outreach email.
// Fires POST /skill-center/jobs/{job_id}/invite-candidate. Persists an
// invitation row so the parent panel can show an "Invited" badge and so
// downstream ROI reports know which hires came from talent-pool sourcing.
// ═══════════════════════════════════════════════════════════════════════════
function InviteToApplyDialog({ jobId, candidate, skill, existing, onClose, onSent }) {
    const { user } = useAuth();
    const hasEmail = candidate.has_email !== false && candidate.has_email !== undefined
        ? Boolean(candidate.has_email)
        : Boolean(candidate.email);
    const defaultSubject = existing?.subject || `Quick question about our open role`;
    const defaultBody = existing?.body || [
        `Hi ${(candidate.name || "there").split(" ")[0]},`,
        `I came across your profile while sourcing for one of our open roles.${
            skill ? ` Your background in ${skill} caught my eye.` : ""
        } Would you be open to a short conversation this week?`,
        `If interested, you can apply directly using the button in this email — it takes under 2 minutes.`,
        `Best,\n${user?.name || user?.email || "The recruiting team"}`,
    ].join("\n\n");

    const [subject, setSubject] = useState(defaultSubject);
    const [body, setBody] = useState(defaultBody);
    const [sendEmail, setSendEmail] = useState(true);
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!subject.trim() || !body.trim()) {
            toast.error("Subject and body cannot be empty");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post(`/skill-center/jobs/${jobId}/invite-candidate`, {
                candidate_id: candidate.id,
                skill: skill || null,
                subject: subject.trim(),
                body: body.trim(),
                send_email: sendEmail,
            });
            if (data.email_status === "sent") {
                toast.success(`Invite sent to ${candidate.name}`);
            } else if (data.email_status === "skipped" && sendEmail) {
                toast.warning(`Invite logged, but email not sent (${data.email_error || "Resend not configured"})`);
            } else {
                toast.success(`Invitation logged for ${candidate.name}`);
            }
            onSent({
                invitation_id: data.invitation_id,
                candidate_id: candidate.id,
                email_status: data.email_status,
                sent_at: data.email_status === "sent" ? new Date().toISOString() : null,
                resend_count: data.resend_count,
            });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg rounded-none border border-border" data-testid="invite-to-apply-dialog">
                <DialogHeader>
                    <DialogTitle className="text-base font-mono uppercase tracking-widest">
                        Invite {candidate.name} to apply
                    </DialogTitle>
                    <DialogDescription className="text-xs text-muted-foreground">
                        {hasEmail
                            ? <>An email will be sent to the candidate&apos;s address on file.</>
                            : <span className="text-amber-700">No email on file — invitation will only be logged.</span>}
                        {skill && <> · Surfaced by skill <span className="font-mono">{skill}</span></>}
                        {existing && <> · Previously invited{existing.resend_count > 0 ? ` (${existing.resend_count} resends)` : ""}</>}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label htmlFor="invite-subject" className="text-[11px] font-mono uppercase tracking-widest">Subject</Label>
                        <Input
                            id="invite-subject"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            className="rounded-none mt-1"
                            maxLength={200}
                            data-testid="invite-subject-input"
                        />
                    </div>
                    <div>
                        <Label htmlFor="invite-body" className="text-[11px] font-mono uppercase tracking-widest">Message</Label>
                        <Textarea
                            id="invite-body"
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            className="rounded-none mt-1 min-h-[200px] font-mono text-[13px]"
                            data-testid="invite-body-input"
                        />
                        <div className="text-[10px] text-muted-foreground mt-1">
                            A clickable &quot;Apply&quot; button is auto-appended to the email.
                        </div>
                    </div>
                    <label className="flex items-center gap-2 text-xs">
                        <input
                            type="checkbox"
                            checked={sendEmail}
                            onChange={(e) => setSendEmail(e.target.checked)}
                            disabled={!hasEmail}
                            data-testid="invite-send-email-toggle"
                        />
                        <span>Send email now {!hasEmail && <span className="text-muted-foreground">(no email on file — will only log)</span>}</span>
                    </label>
                </div>
                <DialogFooter>
                    <Button variant="outline" className="rounded-none" onClick={onClose} disabled={busy} data-testid="invite-cancel-button">
                        Cancel
                    </Button>
                    <Button
                        className="rounded-none bg-ikb hover:bg-ikb/90"
                        onClick={submit}
                        disabled={busy}
                        data-testid="invite-send-button"
                    >
                        {busy ? "Sending…" : (existing ? "Re-invite" : "Send invite")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * SpotlightBadge — recruiter-facing badge shown next to the job title.
 *
 * Two states:
 *   - Highlighted (with valid `spotlight_until` in the future):
 *     colour-tinted badge showing "★ Spotlight · until MMM DD" — click
 *     to open the picker and extend/change the expiry.
 *   - Not highlighted OR expired: subtle "Add spotlight" chip — click
 *     to enable + pick colour/expiry.
 *
 * Uses the shared SpotlightPickerBody so the actual settings UI stays
 * in one place (this component is just the trigger + badge shell).
 */
function SpotlightBadge({ job, onSaved }) {
    const [open, setOpen] = useState(false);
    const su = job?.spotlight_until;
    const nowIso = new Date().toISOString();
    const active = !!job?.highlight && (!su || su > nowIso);
    const expired = !!job?.highlight && !!su && su <= nowIso;
    const color = job?.highlight_color || "#FBBF24";
    const untilText = su ? new Date(su).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null;

    const trigger = active ? (
        <button
            type="button"
            className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border inline-flex items-center gap-1 transition-opacity hover:opacity-80"
            style={{ background: `${color}20`, color, borderColor: color }}
            data-testid="job-spotlight-badge"
            title="Click to extend or edit spotlight"
        >
            <Sparkles className="w-3 h-3" /> Spotlight{untilText && ` · until ${untilText}`}
        </button>
    ) : expired ? (
        <button
            type="button"
            className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border inline-flex items-center gap-1 text-amber-800 border-amber-300 bg-amber-50 hover:bg-amber-100"
            data-testid="job-spotlight-badge-expired"
            title="Spotlight expired — click to re-extend"
        >
            <Sparkles className="w-3 h-3" /> Spotlight expired
        </button>
    ) : (
        <button
            type="button"
            className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-dashed border-border text-muted-foreground hover:text-foreground hover:border-foreground inline-flex items-center gap-1"
            data-testid="job-spotlight-add-btn"
            title="Promote this job on embed boards"
        >
            <Sparkles className="w-3 h-3" /> Add spotlight
        </button>
    );

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
            <PopoverContent className="w-96 p-4" align="start">
                <SpotlightPickerBody
                    job={job}
                    onSaved={(payload) => { onSaved?.(payload); setOpen(false); }}
                    onCancel={() => setOpen(false)}
                />
            </PopoverContent>
        </Popover>
    );
}

